// "Preguntále a Atlas": el botón que abre el panel lateral desde cualquier
// pantalla, llevándose la nota de DÓNDE se tocó.
//
// El contexto no es decorativo: viaja al bloque volátil del system prompt, así
// que el agente sabe que la pregunta salió del Mapa, del Plan o de Marca y
// puede contestar sobre eso sin que el usuario tenga que explicarlo.
//
// Es un botón común en el FLUJO de la pantalla, no un botón flotante: en esta
// app un `fixed` puesto desde una página se mete debajo de la sidebar (el panel
// de contenido de desktop es `fixed`), y un flotante encima del contenido
// taparía justo la esquina de lo que el usuario está mirando.

import { useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useChatAtlasCascaron } from '../ChatAtlasContext';
import { useAccesoAlAgente } from './useAccesoAlAgente';

interface Props {
  /** Etiqueta corta y humana de la pantalla. Ej: "Mapa del estudio de marketing". */
  pantalla: string;
  /** Qué está mirando el usuario, si se sabe. Ej: "nodo Competencia". */
  detalle?: string;
  /** Texto para dejar precargado en el input (NO se envía solo). */
  mensajeInicial?: string;
  /** `solido` (azul) para la acción principal de una pantalla; `sutil`
   *  (contorno) cuando comparte fila con otros controles. */
  variante?: 'solido' | 'sutil';
  className?: string;
}

export const BotonPreguntaleAtlas: React.FC<Props> = ({
  pantalla,
  detalle,
  mensajeInicial,
  variante = 'sutil',
  className = '',
}) => {
  // El contexto CHICO: este botón sólo necesita abrir. Leer el grande lo
  // haría re-renderizar con cada token del stream, desde cualquier pantalla.
  const { abrirChat } = useChatAtlasCascaron();
  const location = useLocation();
  // El Agente es Pro/Ultra (ADR-2026-09-14-el-agente-es-de-pro-y-ultra). Sin acceso el
  // botón no se dibuja: dejarlo y que no pase nada al tocarlo es peor que no ofrecerlo.
  // Hoy las pantallas que lo montan son internas o del Estudio de Video (Ultra), así que
  // en la práctica no cambia nada — es para que no cambie tampoco cuando alguien lo ponga
  // en una pantalla abierta.
  const acceso = useAccesoAlAgente();
  if (acceso !== 'permitido') return null;

  return (
    <button
      type="button"
      onClick={() =>
        abrirChat({
          contexto: { pantalla, ruta: location.pathname, detalle },
          vista: 'panel',
          ...(mensajeInicial !== undefined ? { mensajeInicial } : {}),
        })
      }
      // Padding 2:1 (py-2 / px-4) y sin alto fijo, para que el botón crezca con
      // el texto si algún día cambia la etiqueta.
      className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full py-2 px-4 text-[13px] font-medium transition-colors ${
        variante === 'solido'
          ? 'bg-brand-primary text-white hover:bg-brand-primary-hover'
          : 'border border-[#02062B]/[0.1] bg-white text-brand-black hover:border-[#1644EB]/[0.35] hover:text-brand-primary'
      } ${className}`}
    >
      <Sparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
      Preguntále a Atlas
    </button>
  );
};

export default BotonPreguntaleAtlas;
